/**
 * ClientLogEvent — structured client-side log event type + secret redactor.
 *
 * METADATA ONLY (Constitution III/IX). Every event MUST pass `assertNoPixels`
 * before being buffered or sent. `redactEvent` strips secret-named keys
 * (authorization/cookie/token/password — deep, case-insensitive), request/response
 * bodies, and clamps message/stack to safe sizes.
 *
 * Client observability plan — Task 1.
 */

/** Severity tier. `error`/`warn` are never dropped until server-acked. */
export type LogLevel = "debug" | "info" | "warn" | "error";

/** Structural category of the event (used for filtering in the dashboard). */
export type LogCategory = "error" | "network" | "action" | "lifecycle";

/** Max string lengths for clamping (plan: 4000 / 8000). */
const MAX_MESSAGE = 4_000;
const MAX_STACK = 8_000;

/** Secret key pattern: matches any key that contains these words (case-insensitive). */
// MUST cover at least guard.ts's SECRET_KEY_RE: redaction that misses a key the guard
// scans for means the whole event is dropped by assertNoPixels instead of sanitized —
// exactly the events (secret-store / provider-key failures) an operator most needs to see.
const SECRET_KEY_RE = /authorization|cookie|token|password|secret|api[_-]?key|bearer/i;

/** Body-like field names on request objects that must be stripped. */
const BODY_FIELD_RE = /^(body|response|responseBody|requestBody)$/i;

/**
 * Structured client log event — the atomic unit buffered, synced, and stored
 * in `client_log_events`.
 *
 * Field naming is camelCase on the client; the server normalizes to snake_case.
 */
export interface ClientLogEvent {
  /** UUID, client-generated for idempotent dedup on the server. */
  id: string;
  /** Epoch milliseconds when the event occurred (client clock). */
  occurredAt: number;
  level: LogLevel;
  category: LogCategory;
  /** Human-readable description (clamped to 4000 chars after redaction). */
  message: string;
  /** Error stack trace (clamped to 8000 chars). */
  stack?: string;
  context: {
    /** Current UI route (path only, no query). */
    route?: string;
    /** Network request metadata (path-only URL, no body, no response). */
    request?: {
      method: string;
      urlPath: string;
      status?: number;
      durationMs?: number;
      ok: boolean;
    };
    appVersion: string;
    platform: "web" | "desktop";
    deviceId: string;
    sessionId: string;
    customerId?: string;
    /** Arbitrary structured diagnostic detail attached at the call site (step name,
     *  url host, HTTP status, byte sizes, serialized error, …). Redacted like the rest
     *  of the event; this is what makes an error entry actionable ("trace + details"). */
    detail?: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// Deep-redact helpers
// ---------------------------------------------------------------------------

/** Deep-clone a plain object/array, removing any key matching SECRET_KEY_RE
 *  and any body-like key on a `request` sub-object. */
function deepRedact(obj: unknown, isRequest = false): unknown {
  if (obj === null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => deepRedact(item));
  }

  const record = obj as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    // Strip secret-named keys (everywhere, case-insensitive).
    if (SECRET_KEY_RE.test(key)) continue;
    // Strip body/response fields on request objects.
    if (isRequest && BODY_FIELD_RE.test(key)) continue;
    // Recurse. Mark child as "is request" if the key is "request".
    out[key] = deepRedact(value, key === "request");
  }
  return out;
}

/**
 * Redact a `ClientLogEvent` — returns a NEW event object with:
 *  - All secret-named keys removed from `context` (deep, case-insensitive).
 *  - `request.body` / `request.response` (and similar) stripped.
 *  - `message` clamped to 4000 chars.
 *  - `stack` clamped to 8000 chars.
 *
 * Never mutates the original event.
 */
export function redactEvent(e: ClientLogEvent): ClientLogEvent {
  const redactedContext = deepRedact(e.context, false) as ClientLogEvent["context"];

  return {
    ...e,
    message: e.message.slice(0, MAX_MESSAGE),
    stack: e.stack !== undefined ? e.stack.slice(0, MAX_STACK) : undefined,
    context: redactedContext,
  };
}
